import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, Validators } from '@angular/forms';
import { UnRx } from '@pebula/utils';

@Component({
  selector: 'sg-textarea',
  templateUrl: './sg-textarea.component.html',
  styleUrls: ['./sg-textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@UnRx()
export class SgTextareaComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() variant: 'primary' | 'secondary' = 'primary';
  @Input() width: string = '100%';
  @Input() label: string = 'Label';
  @Input() placeholder: string = null;
  @Input() hideLabel: boolean = false;
  @Input() errorsMap: { [error: 'minlength' | 'maxlength' | 'pattern' | string]: string | RegExp } = {};
  @Input() hint: string = null;
  @Input() maxLimit: number = null;
  @Input() rows: number = 3;
  @Input() minRows: number = null;
  @Input() maxRows: number = null;
  @Input() autoResize: boolean = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() errorFallbackMessage: string = 'Invalid input';
  @Input() value: string = '';

  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<string>();

  internalFormControl = new FormControl('');
  isInputFocused: boolean = false;

  get isRequired(): boolean {
    return (
      this.ngControl?.control?.hasValidator?.(Validators.required) ||
      this.internalFormControl?.hasValidator(Validators.required) ||
      false
    );
  }

  get valueLength(): number {
    return this.internalFormControl?.value?.length ?? 0;
  }

  get errorsEntries(): string[] {
    const internalErrors = this.internalFormControl?.errors ? Object.keys(this.internalFormControl.errors) : [];
    const parentErrors = this.ngControl?.control?.errors ? Object.keys(this.ngControl.control.errors) : [];

    return [...new Set([...internalErrors, ...parentErrors])];
  }

  get hasErrors(): boolean {
    const internalInvalid =
      this.internalFormControl?.invalid && (this.internalFormControl?.dirty || this.internalFormControl?.touched);
    const parentInvalid =
      this.ngControl?.control?.invalid && (this.ngControl?.control?.dirty || this.ngControl?.control?.touched);

    return internalInvalid || parentInvalid || false;
  }

  get effectivePlaceholder(): string | null {
    if (!this.hideLabel && this.label && !this.internalFormControl.value && !this.isInputFocused) {
      return null;
    }

    return this.placeholder;
  }

  get effectiveMinRows(): number {
    return this.minRows ?? this.rows;
  }

  get effectiveMaxRows(): number {
    return this.maxRows ?? (this.autoResize ? 10 : this.rows);
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._registerValidators();

    // Set initial value if provided (for non-reactive-forms usage)
    if (this.value && !this.ngControl) {
      this.internalFormControl.setValue(this.value, { emitEvent: false });
    }

    this.internalFormControl.valueChanges.pipe(UnRx(this)).subscribe((value) => {
      this.onChange(value);
      this.valueChange.emit(value);

      if (this.ngControl?.control && this.ngControl.control.value !== value) {
        this.ngControl.control.setValue(value, { emitEvent: false });
      }
    });

    if (this.ngControl?.control) {
      this.ngControl.control.statusChanges.pipe(UnRx(this)).subscribe(() => {
        this._registerValidators();
      });
    }

    if (this.width) {
      this._setCustomProperties();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.width) {
      this._setCustomProperties();
    }

    // Handle value input changes for non-reactive-forms usage
    if (changes.value && !this.ngControl) {
      this.internalFormControl.setValue(changes.value.currentValue, { emitEvent: false });
    }
  }

  writeValue(value: any): void {
    this.internalFormControl.setValue(value, { emitEvent: false });
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.internalFormControl.disable() : this.internalFormControl.enable();
  }

  onInputBlur(): void {
    this.isInputFocused = false;
    this.onTouched();
    this.blur.emit();

    if (this.ngControl?.control) {
      this.ngControl.control.markAsTouched();
      this.ngControl.control.updateValueAndValidity();
    }
  }

  onInputFocus(): void {
    this.isInputFocused = true;
    this.focus.emit();
  }

  getErrorMessage(): string {
    if (this.ngControl?.control?.errors?.required || this.internalFormControl.errors?.required) {
      return 'Required';
    }

    const firstError = this.errorsEntries[0];
    if (firstError && this.errorsMap?.[firstError]) {
      return this.errorsMap[firstError] as string;
    }

    return this.errorFallbackMessage;
  }

  private _registerValidators(): void {
    if (this.ngControl?.control && this.ngControl.control.validator !== this.internalFormControl.validator) {
      this.internalFormControl.setValidators(this.ngControl.control.validator);
      this.internalFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private _setCustomProperties(): void {
    const element = this.elementRef.nativeElement;
    element.style.setProperty('--sg-textarea-width', this.width);
  }
}
